import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

interface HeaderProps {
  darkMode: boolean;
  onToggleDarkMode: () => void;
  userMode: 'demo' | 'full';
  onSignOut?: () => void;
  userProfile?: any;
}

const Header: React.FC<HeaderProps> = ({ darkMode, onToggleDarkMode, userMode, onSignOut, userProfile }) => {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const toggleUserMenu = () => {
    console.log('Toggle user menu clicked, current state:', showUserMenu);
    setShowUserMenu(!showUserMenu);
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleSignOut = async () => {
    try {
      if (onSignOut) {
        await onSignOut();
      }
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showUserMenu]);

  return (
    <header className={`${darkMode ? 'bg-gray-800/95 backdrop-blur-sm' : 'bg-white/95 backdrop-blur-sm'} shadow-sm border-b ${darkMode ? 'border-gray-700' : 'border-slate-200'} sticky top-0 z-40`}>
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
        <div className="flex justify-between items-center py-2 sm:py-3">
          <div className="flex items-center space-x-1 sm:space-x-2 lg:space-x-4">
            <Link 
              to={userMode === 'full' ? '/dashboard' : '/'} 
              className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center bg-gradient-to-br from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 transition-colors cursor-pointer shadow-sm"
            >
              <span className="font-bold text-sm sm:text-base">R</span>
            </Link>
            <div className="flex items-center space-x-1 sm:space-x-2 lg:space-x-3">
              <Link 
                to={userMode === 'full' ? '/dashboard' : '/'} 
                className={`text-base sm:text-lg lg:text-xl font-semibold transition-colors cursor-pointer ${darkMode ? 'text-white hover:text-slate-200' : 'text-slate-900 hover:text-slate-700'}`}
              >
                RealEstart.io
              </Link>
              {/* Hide premium banner on mobile */}
              {userMode === 'full' && userProfile && (
                <span className={`hidden sm:inline-block px-2.5 py-1 rounded-lg text-xs font-medium ${
                  userProfile.subscription_type === 'trial' 
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {userProfile.subscription_type === 'trial' ? '3-Day Trial' : 'Premium'}
                </span>
              )}
              {/* Hide demo banner on mobile */}
              {userMode === 'demo' && (
                <span className="hidden sm:inline-block px-2.5 py-1 rounded-lg text-xs font-medium bg-slate-200 text-slate-700">
                  Demo Mode
                </span>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-1 sm:space-x-2 lg:space-x-4 min-w-0">
            {/* Always show sign-in/trial button for non-authenticated users or demo mode */}
            {(!userProfile || userMode === 'demo') && (
              <>
                <Link to="/checkout?plan=trial" className={`px-3 sm:px-4 py-2 rounded-lg transition-colors font-medium text-sm ${darkMode ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-slate-800 hover:bg-slate-900 text-white'}`}>
                  Start 3-Day Trial
                </Link>
              </>
            )}
            
            {/* Full Mode Navigation */}
            {userMode === 'full' && (
              <>
                {/* Dashboard Icon */}
                <button 
                  data-tour="header-dashboard"
                  onClick={() => navigate('/dashboard')}
                  className={`${darkMode ? 'text-slate-300 hover:text-white bg-slate-700/80 hover:bg-slate-700' : 'text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200'} transition-colors p-2.5 rounded-lg`}
                  title="Learning Dashboard"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </button>
                
                {/* Dark Mode Toggle */}
                <button 
                  data-tour="header-theme"
                  onClick={onToggleDarkMode}
                  className={`${darkMode ? 'text-slate-300 hover:text-white bg-slate-700/80 hover:bg-slate-700' : 'text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200'} transition-colors p-2.5 rounded-lg`}
                  title="Toggle Dark Mode"
                >
                  {darkMode ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path fillRule="evenodd" d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
                
                {/* User Menu */}
                <div className="relative user-menu-container" data-tour="header-account">
                  <button 
                    onClick={toggleUserMenu}
                    className={`flex items-center gap-2 sm:gap-2.5 px-2.5 py-2 sm:px-3 sm:py-2 rounded-lg border transition-colors ${darkMode ? 'bg-slate-800 border-slate-600 hover:bg-slate-700 hover:border-slate-500' : 'bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300'}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-semibold shrink-0 ${darkMode ? 'bg-blue-900/50 text-blue-200' : 'bg-slate-100 text-slate-700'}`}>
                      <span>{userProfile ? getInitials(userProfile.name) : 'U'}</span>
                    </div>
                    <span className={`hidden md:block text-sm font-medium max-w-[120px] truncate ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>{userProfile ? userProfile.name : 'User'}</span>
                    <svg className={`w-4 h-4 shrink-0 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showUserMenu && (
                    <div className={`absolute right-0 mt-2 w-52 ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg py-1.5 z-[9999] border ${darkMode ? 'border-gray-700' : 'border-slate-200'}`}>
                      <button 
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg mx-1 ${darkMode ? 'text-slate-200 hover:bg-slate-700' : 'text-slate-800 hover:bg-slate-50'} transition-colors cursor-pointer text-left`}
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/dashboard');
                        }}
                      >
                        <svg className="w-4 h-4 text-slate-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                        Dashboard
                      </button>
                      <button 
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg mx-1 ${darkMode ? 'text-slate-200 hover:bg-slate-700' : 'text-slate-800 hover:bg-slate-50'} transition-colors cursor-pointer text-left`}
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/profile');
                        }}
                      >
                        <svg className="w-4 h-4 text-slate-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Profile & Settings
                      </button>

                      <hr className={`my-1.5 ${darkMode ? 'border-gray-700' : 'border-slate-200'}`} />
                      <button 
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg mx-1 ${darkMode ? 'text-rose-400 hover:bg-rose-900/20' : 'text-rose-600 hover:bg-rose-50'} transition-colors cursor-pointer text-left`}
                        onClick={() => {
                          setShowUserMenu(false);
                          handleSignOut();
                        }}
                      >
                        <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
            
            {/* Dark Mode Toggle (Demo mode only) */}
            {userMode === 'demo' && (
              <button 
                onClick={onToggleDarkMode}
                className={`${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'} transition-colors p-2 rounded-lg hover:bg-opacity-10 ${darkMode ? 'hover:bg-white' : 'hover:bg-gray-800'}`}
                title="Toggle Dark Mode"
              >
                {darkMode ? (
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
